// Forms — board section 03: every input primitive plus the Field wrapper that
// labels it. Controlled components get a small stateful demo wrapper so the
// section itself stays a pure render.

import { useState } from 'react';
import {
  Checkbox,
  Field,
  Icon,
  Input,
  MembersPicker,
  MentionTextarea,
  OtpInput,
  PasswordInput,
  PasswordStrength,
  PhoneInput,
  Radio,
  Select,
  SelectDropdown,
  Slider,
  Switch,
  TagsPicker,
  Textarea,
} from '@/components/common';
import type { SelectDropdownProps } from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import Spec from './Spec';

const TEAM_OPTIONS = [
  { value: 'a', label: 'Crew A · Downtown' },
  { value: 'b', label: 'Crew B · Uptown' },
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

/** Same four priorities with severity glyphs — demos the per-option `icon`. */
const PRIORITY_ICON_OPTIONS = [
  { value: 'urgent', label: 'Urgent', icon: 'signal' },
  { value: 'high', label: 'High', icon: 'chevrons-up' },
  { value: 'normal', label: 'Normal', icon: 'equal' },
  { value: 'low', label: 'Low', icon: 'chevron-down' },
];

/** Long enough to overflow `maxHeight`, and one label long enough to truncate. */
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'review', label: 'Awaiting supervisor review' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked — waiting on site access' },
  { value: 'done', label: 'Done' },
  { value: 'archived', label: 'Archived' },
];

const DEMO_TAGS = [
  { id: 't1', title: 'Safety' },
  { id: 't2', title: 'Electrical' },
  { id: 't3', title: 'HVAC' },
  { id: 't4', title: 'Quarterly' },
];

const MENTION_OPTIONS = [
  { value: 'user-name', display: 'User Name', icon: 'user' },
  { value: 'company-name', display: 'Company Name', icon: 'building-2' },
  { value: 'otp-code', display: 'OTP Code', icon: 'key-round' },
];

/** SelectDropdown is fully controlled — every cell keeps its own value. */
const SelectDropdownDemo = ({
  initial = 'high',
  ...rest
}: { initial?: string | null } & Partial<SelectDropdownProps>) => {
  const [value, setValue] = useState<string | null>(initial);
  return <SelectDropdown options={PRIORITY_OPTIONS} {...rest} value={value} onChange={setValue} />;
};

/** Phone number + dial code are one controlled pair. */
const PhoneDemo = ({
  error = false,
  readOnly = false,
}: {
  error?: boolean;
  readOnly?: boolean;
}) => {
  const [dial, setDial] = useState(defaultCountry.dial);
  const [digits, setDigits] = useState('551234567');
  return (
    <PhoneInput
      countries={countries}
      dialCode={dial}
      onDialCodeChange={setDial}
      value={digits}
      onChange={setDigits}
      error={error}
      readOnly={readOnly}
    />
  );
};

/** Password + live strength meter, the pairing used on the auth screens. */
const PasswordDemo = () => {
  const [password, setPassword] = useState('Midani24');
  return (
    <>
      <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
      <PasswordStrength password={password} />
    </>
  );
};

const OtpDemo = ({ length = 4, error = false }: { length?: number; error?: boolean }) => {
  const [code, setCode] = useState('12');
  return <OtpInput length={length} value={code} onChange={setCode} error={error} />;
};

const SliderDemo = () => {
  const [value, setValue] = useState(40);
  return (
    <div className='theme-page__meter-row'>
      <Slider value={value} onChange={(e) => setValue(Number(e.target.value))} />
      <span className='theme-page__token-value'>{value}</span>
    </div>
  );
};

/** `#` opens the variable menu; the stored value keeps `#[Display]#` tokens. */
const MentionDemo = () => {
  const [value, setValue] = useState('Hi #[User Name]# — your code is ');
  const [map, setMap] = useState<Record<string, string>>({ 'User Name': 'user-name' });
  return (
    <MentionTextarea
      value={value}
      mentionMap={map}
      options={MENTION_OPTIONS}
      onChangeValue={(next, nextMap) => {
        setValue(next);
        setMap(nextMap);
      }}
    />
  );
};

const TagsPickerDemo = () => {
  const [ids, setIds] = useState<string[]>(['t1']);
  return (
    <TagsPicker tags={DEMO_TAGS} selectedIds={ids} onChange={setIds} placeholder='Select tags' />
  );
};

const MembersPickerDemo = () => {
  const [userIds, setUserIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  return (
    <MembersPicker
      selectedUserIds={userIds}
      selectedGroupIds={groupIds}
      onChangeUsers={setUserIds}
      onChangeGroups={setGroupIds}
    />
  );
};

const FormsSection = () => (
  <section className='theme-page__section'>
    <h2 className='theme-page__section-title'>
      <Icon name='text-cursor-input' size={18} /> 03 · Forms
    </h2>

    {/* ---- Field ---- */}
    <h3 className='theme-page__block-title'>
      Field · label, marker &amp; message
      <span className='theme-page__note'>
        `error` outranks `hint`; `required` outranks `optional`.
      </span>
    </h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Site name'>
        <Input iconStart='map-pin' placeholder='e.g. Warehouse North' />
      </Field>
      <Field label='Site name' required>
        <Input iconStart='map-pin' placeholder='required' />
      </Field>
      <Field label='Site name' optional>
        <Input iconStart='map-pin' placeholder='optional' />
      </Field>
      <Field
        label='Reference'
        labelTag={
          <>
            <Icon name='lock' size={12} /> Automatic
          </>
        }
      >
        <Input defaultValue='INS-2048' readOnly />
      </Field>
      <Field label='Notes' hint='Visible to the whole crew.'>
        <Input placeholder='Add a note' />
      </Field>
      <Field label='Password' error='Must be at least 8 characters'>
        <Input iconStart='lock' type='password' error />
      </Field>
    </div>

    {/* ---- Input ---- */}
    <h3 className='theme-page__block-title'>Input · icons, sizes &amp; states</h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Default (md)'>
        <Input iconStart='search' placeholder='Search sites' />
      </Field>
      <Field label="size='sm'">
        <Input size='sm' iconStart='search' placeholder='Search sites' />
      </Field>
      <Field label='iconEnd'>
        <Input iconEnd='calendar' placeholder='Pick a date' />
      </Field>
      <Field label='iconStart + iconEnd'>
        <Input iconStart='search' iconEnd='x' defaultValue='Floor inspection' />
      </Field>
      <Field label='Focused' hint='Demo-only class — the real ring comes from :focus.'>
        <Input iconStart='mail' defaultValue='u.s@falconfm.co' className='is-demo-focused' />
      </Field>
      <Field label='error'>
        <Input iconStart='mail' defaultValue='not-an-email' error />
      </Field>
      <Field label='disabled'>
        <Input iconStart='lock' defaultValue='Locked value' disabled />
      </Field>
      <Field label='readOnly'>
        <Input defaultValue='Read-only value' readOnly />
      </Field>
      <Field label="type='date'">
        <Input type='date' defaultValue='2026-08-31' />
      </Field>
    </div>

    {/* ---- Select / Textarea ---- */}
    <h3 className='theme-page__block-title'>Select &amp; Textarea</h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Team'>
        <Select iconStart='users' defaultValue='a' options={TEAM_OPTIONS} />
      </Field>
      <Field label="size='sm'">
        <Select size='sm' iconStart='users' defaultValue='a' options={TEAM_OPTIONS} />
      </Field>
      <Field label='error'>
        <Select iconStart='users' defaultValue='a' options={TEAM_OPTIONS} error />
      </Field>
      <Field label='disabled'>
        <Select iconStart='users' defaultValue='a' options={TEAM_OPTIONS} disabled />
      </Field>
      <Field label='Notes'>
        <Textarea defaultValue='Inspected loading bay — all clear.' />
      </Field>
      <Field label='error'>
        <Textarea defaultValue='Too short.' error />
      </Field>
      <Field label='disabled'>
        <Textarea defaultValue='Locked note.' disabled />
      </Field>
      <Field label='rows={6}'>
        <Textarea rows={6} placeholder='Longer description…' />
      </Field>
      <Field label='MentionTextarea' hint='Type # to insert a variable.'>
        <MentionDemo />
      </Field>
    </div>

    {/* ---- SelectDropdown ---- */}
    <h3 className='theme-page__block-title'>
      SelectDropdown
      <span className='theme-page__note'>
        Custom single select — styled rows, panel matches the trigger. Its size ladder follows
        Button (sm 2.5rem / md 3rem / lg 3.5rem), so `md` is shorter than a `md` Input.
      </span>
    </h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Priority'>
        <SelectDropdownDemo />
      </Field>
      <Field label="size='lg'">
        <SelectDropdownDemo size='lg' />
      </Field>
      <Field label="size='sm'">
        <SelectDropdownDemo size='sm' />
      </Field>
      <Field label='iconStart'>
        <SelectDropdownDemo iconStart='flag' />
      </Field>
      <Field label='option icon'>
        <SelectDropdownDemo options={PRIORITY_ICON_OPTIONS} />
      </Field>
      <Field label="option icon · size='lg'">
        <SelectDropdownDemo options={PRIORITY_ICON_OPTIONS} size='lg' />
      </Field>
      <Field label='placeholder' hint='value={null}'>
        <SelectDropdownDemo initial={null} />
      </Field>
      <Field label='error' error='Pick a priority.'>
        <SelectDropdownDemo initial={null} error />
      </Field>
      <Field label='disabled'>
        <SelectDropdownDemo disabled />
      </Field>
      <Field label='disabled option'>
        <SelectDropdownDemo
          options={PRIORITY_OPTIONS.map((o) =>
            o.value === 'urgent' ? { ...o, disabled: true } : o,
          )}
        />
      </Field>
      <Field label="align='start'">
        <SelectDropdownDemo align='start' />
      </Field>
      <Field label="align='end'">
        <SelectDropdownDemo align='end' />
      </Field>
      <Field label='portal' hint='Escapes a clipping ancestor.'>
        <SelectDropdownDemo portal />
      </Field>
      <Field label='maxHeight={140}' hint='Long labels truncate; the list scrolls.'>
        <SelectDropdownDemo options={STATUS_OPTIONS} initial='review' maxHeight={140} />
      </Field>
      <Field label='options={[]}'>
        <SelectDropdownDemo options={[]} initial={null} />
      </Field>
    </div>

    {/* ---- Auth inputs ---- */}
    <h3 className='theme-page__block-title'>
      Auth inputs
      <span className='theme-page__note'>
        PhoneInput and OtpInput force LTR so digit order never flips.
      </span>
    </h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Password + strength'>
        <PasswordDemo />
      </Field>
      <Field label='PasswordInput · error'>
        <PasswordInput defaultValue='short' error />
      </Field>
      <Field label='Phone'>
        <PhoneDemo />
      </Field>
      <Field label='PhoneInput · error'>
        <PhoneDemo error />
      </Field>
      <Field label='PhoneInput · readOnly'>
        <PhoneDemo readOnly />
      </Field>
      <Field label='OtpInput · length={4}'>
        <OtpDemo />
      </Field>
      <Field label='OtpInput · length={6}'>
        <OtpDemo length={6} />
      </Field>
      <Field label='OtpInput · error'>
        <OtpDemo error />
      </Field>
    </div>

    {/* ---- Selection controls ---- */}
    <h3 className='theme-page__block-title'>Selection controls</h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <div className='theme-page__col'>
        <p className='theme-page__card-title'>Checkbox</p>
        <Checkbox label='Offline sync enabled' defaultChecked />
        <Checkbox label='Email me a summary' />
        <Checkbox label="size='sm'" size='sm' defaultChecked />
        <Checkbox label='disabled' disabled />
        <Checkbox label='disabled · checked' defaultChecked disabled />
      </div>
      <div className='theme-page__col'>
        <p className='theme-page__card-title'>Radio</p>
        <Radio name='freq' label='Daily' defaultChecked />
        <Radio name='freq' label='Weekly' />
        <Radio name='freq' label='Monthly' />
        <Radio name='freq-off' label='disabled' disabled />
        <Radio name='freq-off2' label='disabled · checked' defaultChecked disabled />
      </div>
      <div className='theme-page__col'>
        <p className='theme-page__card-title'>Switch</p>
        <Switch label='Live tracking' defaultChecked />
        <Switch label='Auto-archive' />
        <Switch label="size='sm'" size='sm' defaultChecked />
        <Switch label="size='lg'" size='lg' defaultChecked />
        <Switch label='disabled' disabled />
        <Switch label='disabled · on' defaultChecked disabled />
      </div>
    </div>

    <h3 className='theme-page__block-title'>Slider</h3>
    <div className='theme-page__col'>
      <SliderDemo />
      <div className='theme-page__variants'>
        <Spec label='min=0 max=10 step=1'>
          <Slider value={7} min={0} max={10} step={1} readOnly />
        </Spec>
        <Spec label='disabled'>
          <Slider value={30} disabled />
        </Spec>
      </div>
    </div>

    {/* ---- Composite pickers ---- */}
    <h3 className='theme-page__block-title'>
      Composite pickers
      <span className='theme-page__note'>
        MembersPicker self-fetches users/groups; TagsPicker is fully controlled.
      </span>
    </h3>
    <div className='theme-page__grid theme-page__grid--inputs'>
      <Field label='Members'>
        <MembersPickerDemo />
      </Field>
      <Field label='Tags'>
        <TagsPickerDemo />
      </Field>
      <Field label='Tags · disabled'>
        <TagsPicker
          tags={DEMO_TAGS}
          selectedIds={[]}
          onChange={() => {}}
          placeholder='Select tags'
          disabled
        />
      </Field>
    </div>
  </section>
);

export default FormsSection;
